import { useEffect, useState, useMemo, useRef, Fragment } from "react";
import { api } from "../lib/api";
import { Plus, Trash2, X, UserCog, ShieldCheck, Sparkles, Save, RotateCcw, KeyRound, Info, LayoutTemplate, FilePlus2, Pencil, FileText, LayoutDashboard, Users as UsersIcon, Clock, Timer } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { useModalGuard, UnsavedGuardBar } from "../hooks/useModalGuard";
import { useSortState, sortRows, SortTh } from "../components/ui/sort";
import { useCabangOptions } from "../lib/cabang";

const ACTIONS = [
  { key: "view",   label: "Lihat",  color: "sky" },
  { key: "create", label: "Input",  color: "emerald" },
  { key: "update", label: "Edit",   color: "amber" },
  { key: "delete", label: "Hapus",  color: "rose" },
];

const ROLES = [
  { value: "custom",     label: "Custom (set matrix manual)", tag: "violet" },
  { value: "viewer",     label: "Viewer (default: hanya lihat)", tag: "slate" },
  { value: "admin",      label: "Admin (default: akses penuh)", tag: "sky" },
  { value: "superadmin", label: "Superadmin (bypass semua check)", tag: "emerald" },
];

const roleColor = (role) => ({
  superadmin: "bg-emerald-100 text-emerald-800",
  admin:      "bg-sky-100 text-sky-800",
  viewer:     "bg-slate-100 text-slate-700",
  employee:   "bg-orange-100 text-orange-700",
  custom:     "bg-violet-100 text-violet-800",
}[role] || "bg-slate-100 text-slate-700");

// Durasi izin sementara (poin 13 owner: "beri akses edit faktur 5 menit").
// Dikirim sebagai MENIT ke backend — backend yang menghitung waktu berakhir
// dari jam SERVER, bukan dari timestamp yang dihitung di browser.
const TEMP_DURATIONS = [
  { value: "5",    label: "5 menit" },
  { value: "15",   label: "15 menit" },
  { value: "30",   label: "30 menit" },
  { value: "60",   label: "1 jam" },
  { value: "240",  label: "4 jam" },
  { value: "1440", label: "1 hari" },
];

/** Format sisa waktu sebuah timestamp ISO relatif ke `nowMs`. Murni tampilan —
 *  penegakan sesungguhnya selalu di backend (authorize_module dicek ulang
 *  dari DB tiap request), jadi selisih jam perangkat tidak berbahaya di sini. */
function formatSisaWaktu(iso, nowMs) {
  if (!iso) return "";
  const target = new Date(iso).getTime();
  if (Number.isNaN(target)) return "";
  const ms = target - nowMs;
  if (ms <= 0) return "Kedaluwarsa";
  const totalSec = Math.floor(ms / 1000);
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  if (h > 0) return `${h}j ${m}m lagi`;
  if (m > 0) return `${m}m ${s}d lagi`;
  return `${s}d lagi`;
}

export default function Users() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [modules, setModules] = useState([]);
  const [scopedModules, setScopedModules] = useState([]);
  const [dashboardWidgets, setDashboardWidgets] = useState([]);
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "viewer", template_key: "" });
  // Permission editor state
  const [permModal, setPermModal] = useState(null); // {user, effective, explicit, has_override}
  const [permMatrix, setPermMatrix] = useState({});
  const [permWidgets, setPermWidgets] = useState(null); // null=all, array=filtered subset
  const [permSaving, setPermSaving] = useState(false);
  const [permTemplates, setPermTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState("");
  // Custom template CRUD modal (superadmin only)
  const [tplModal, setTplModal] = useState(null); // { mode, key, label, description, permissions }
  const [tplSaving, setTplSaving] = useState(false);
  // Izin sementara / berjangka waktu (poin 13 owner)
  const [tempForm, setTempForm] = useState({ module: "", actions: [], duration: "5" });
  const [tempSaving, setTempSaving] = useState(false);
  const [nowTick, setNowTick] = useState(() => Date.now());
  // Reset password akun lain (brief owner poin 5, 16 Agu 2026) — password TIDAK
  // PERNAH bisa ditampilkan (hash bcrypt satu arah), hanya bisa DIGANTI.
  const [pwModal, setPwModal] = useState(null); // user yang sedang diganti passwordnya
  const [pwForm, setPwForm] = useState({ new_password: "", confirm: "" });
  const [pwSaving, setPwSaving] = useState(false);

  const isSuperadmin = user?.role === "superadmin";
  const scopedSet = useMemo(() => new Set(scopedModules), [scopedModules]);
  const customTemplates = useMemo(() => permTemplates.filter(t => t.is_custom), [permTemplates]);
  const reloadTemplates = () => api.get("/permissions/templates").then(r => setPermTemplates(r.data?.templates || []));

  const load = () => api.get("/users").then(r => setItems(r.data));
  useEffect(() => {
    load();
    api.get("/permissions/modules").then(r => {
      setModules(r.data?.modules || []);
      setScopedModules(r.data?.scoped_modules || []);
      setDashboardWidgets(r.data?.dashboard_widgets || []);
    });
    api.get("/permissions/templates").then(r => setPermTemplates(r.data?.templates || []));
  }, []);

  const grouped = useMemo(() => {
    const g = {};
    modules.forEach(m => { g[m.group] = g[m.group] || []; g[m.group].push(m); });
    return g;
  }, [modules]);

  const save = async () => {
    try {
      const payload = { ...form };
      if (!payload.template_key) delete payload.template_key;
      await api.post("/users", payload);
      const tplLbl = form.template_key
        ? permTemplates.find(t => t.key === form.template_key)?.label
        : null;
      toast.success(tplLbl ? `User ditambahkan dengan template ${tplLbl}` : "User ditambahkan");
      setModal(false);
      setForm({ name: "", email: "", password: "", role: "viewer", template_key: "" });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
  };

  const del = async (id) => {
    if (!confirm("Hapus user?")) return;
    try { await api.delete(`/users/${id}`); load(); }
    catch (e) { toast.error(e?.response?.data?.detail); }
  };

  // ---- Reset password akun lain (poin 5 owner) ----
  const bukaResetPassword = (u) => {
    setPwForm({ new_password: "", confirm: "" });
    setPwModal(u);
  };

  const submitResetPassword = async () => {
    if (!pwModal) return;
    if (pwForm.new_password.length < 6) { toast.error("Password baru minimal 6 karakter"); return; }
    if (pwForm.new_password !== pwForm.confirm) { toast.error("Konfirmasi password tidak cocok"); return; }
    setPwSaving(true);
    try {
      const r = await api.post(`/users/${pwModal.id}/reset-password`, { new_password: pwForm.new_password });
      toast.success(r?.data?.message || "Password berhasil diganti");
      setPwModal(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal mengganti password");
    } finally { setPwSaving(false); }
  };

  // Pembatasan GUDANG per akun (brief 4 Sep 2026 P16, "dibatasi per gudang"):
  // memakai mekanisme yang SUDAH ADA di backend — `users.allowed_branches` →
  // `allowed_warehouse_ids()` — yang sampai sekarang tidak punya satu pun UI
  // (komentar users.py: "kodenya membaca allowed_branches yang tak pernah
  // terisi"). Kosong = semua gudang, persis perilaku lama.
  const cabangOptions = useCabangOptions();
  const [permBranches, setPermBranches] = useState([]);
  const [permBranchesAwal, setPermBranchesAwal] = useState([]);
  const openPermEditor = async (u) => {
    if (u.role === "superadmin") {
      toast.info("Superadmin selalu memiliki akses penuh — tidak bisa dibatasi");
      return;
    }
    try {
      const { data } = await api.get(`/users/${u.id}/permissions`);
      setPermModal(data);
      // Pilihan template dari modal SEBELUMNYA (user lain) tidak boleh terbawa: savePerm
      // mengirimnya sebagai template_key, yang menentukan halaman awal akun (G8 butir b).
      setSelectedTemplate("");
      setPermBranches(Array.isArray(u.allowed_branches) ? u.allowed_branches : []);
      setPermBranchesAwal(Array.isArray(u.allowed_branches) ? u.allowed_branches : []);
      setPermMatrix(data.effective);
      setPermWidgets(Array.isArray(data.dashboard_widgets) ? data.dashboard_widgets : null);
      setTempForm({ module: "", actions: [], duration: "5" });
    } catch (e) {
      toast.error("Gagal memuat izin: " + (e?.response?.data?.detail || ""));
    }
  };

  // Tik tiap detik SELAMA modal izin terbuka, supaya badge "sisa waktu" izin
  // sementara hidup tanpa perlu refresh manual. Tidak berjalan sama sekali
  // kalau modal tertutup — tidak ada biaya untuk user tanpa izin sementara.
  useEffect(() => {
    if (!permModal) return;
    const t = setInterval(() => setNowTick(Date.now()), 1000);
    return () => clearInterval(t);
  }, [permModal]);

  // Daftar semua (modul, aksi) yang pernah diberi batas waktu untuk user yang
  // sedang dibuka — termasuk yang SUDAH kedaluwarsa (supaya admin tetap lihat
  // riwayatnya & bisa jadikan permanen lagi kalau ternyata itu keliru).
  const timeBoxedEntries = useMemo(() => {
    const out = [];
    Object.entries(permMatrix).forEach(([modKey, row]) => {
      if (!row?.expires) return;
      Object.entries(row.expires).forEach(([action, ts]) => {
        if (!row[action]) return; // aksinya sendiri sudah dicabut — expiry basi, jangan ditampilkan
        out.push({ modKey, action, expiresAt: ts });
      });
    });
    return out;
  }, [permMatrix]);

  const toggleTempAction = (a) => {
    setTempForm(f => ({
      ...f,
      actions: f.actions.includes(a) ? f.actions.filter(x => x !== a) : [...f.actions, a],
    }));
  };

  const refreshPermModal = async () => {
    if (!permModal) return;
    const { data } = await api.get(`/users/${permModal.user.id}/permissions`);
    setPermModal(data);
    setPermMatrix(data.effective);
  };

  const submitTempGrant = async () => {
    if (!permModal) return;
    if (!tempForm.module) { toast.error("Pilih modul dulu"); return; }
    if (tempForm.actions.length === 0) { toast.error("Pilih minimal satu aksi (lihat/input/edit/hapus)"); return; }
    setTempSaving(true);
    try {
      const duration_minutes = tempForm.duration === "permanent" ? null : Number(tempForm.duration);
      const r = await api.post(`/users/${permModal.user.id}/permissions/temporary`, {
        module: tempForm.module, actions: tempForm.actions, duration_minutes,
      });
      const modLabel = modules.find(m => m.key === tempForm.module)?.label || tempForm.module;
      toast.success(duration_minutes
        ? `Izin "${modLabel}" diberikan selama ${TEMP_DURATIONS.find(d => d.value === tempForm.duration)?.label}`
        : `Izin "${modLabel}" dijadikan permanen`);
      await refreshPermModal();
      setTempForm({ module: "", actions: [], duration: "5" });
      void r;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal memberi izin sementara");
    } finally { setTempSaving(false); }
  };

  const jadikanPermanen = async (modKey, action) => {
    if (!permModal) return;
    try {
      await api.post(`/users/${permModal.user.id}/permissions/temporary`, {
        module: modKey, actions: [action], duration_minutes: null,
      });
      toast.success("Izin dijadikan permanen (batas waktu dicabut)");
      await refreshPermModal();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal");
    }
  };

  const setScope = (modKey, scope) => {
    setPermMatrix(m => ({
      ...m,
      [modKey]: { ...(m[modKey] || {}), scope },
    }));
  };

  const toggleWidget = (widgetKey) => {
    setPermWidgets(w => {
      const cur = Array.isArray(w) ? w : dashboardWidgets.map(x => x.key); // start from all if unset
      return cur.includes(widgetKey)
        ? cur.filter(k => k !== widgetKey)
        : [...cur, widgetKey];
    });
  };

  const toggleAllWidgets = (on) => {
    setPermWidgets(on ? dashboardWidgets.map(w => w.key) : []);
  };

  const resetWidgetsToDefault = () => {
    setPermWidgets(null); // null = show all default (no filter)
    toast.info("Widget dikembalikan ke default (semua tampil)");
  };

  const togglePerm = (modKey, action, value) => {
    setPermMatrix(m => ({
      ...m,
      [modKey]: { ...(m[modKey] || {}), [action]: value },
    }));
  };

  const toggleModuleAll = (modKey, value) => {
    setPermMatrix(m => ({
      ...m,
      [modKey]: { view: value, create: value, update: value, delete: value },
    }));
  };

  const toggleActionAll = (action, value) => {
    setPermMatrix(m => {
      const nm = { ...m };
      modules.forEach(mod => {
        nm[mod.key] = { ...(nm[mod.key] || {}), [action]: value };
      });
      return nm;
    });
  };

  const applyPreset = (preset) => {
    const nm = {};
    modules.forEach(mod => {
      if (preset === "all")      nm[mod.key] = { view: true,  create: true,  update: true,  delete: true };
      else if (preset === "read") nm[mod.key] = { view: true,  create: false, update: false, delete: false };
      else if (preset === "none") nm[mod.key] = { view: false, create: false, update: false, delete: false };
    });
    setPermMatrix(nm);
  };

  // ---- Custom Template CRUD ops ----
  const tplLabelRef = useRef(null);
  const tplEditorRef = useRef(null);

  const openNewTemplate = () => {
    const emptyMatrix = {};
    modules.forEach(m => { emptyMatrix[m.key] = { view: false, create: false, update: false, delete: false }; });
    setTplModal({ mode: "new", key: "", label: "", description: "", permissions: emptyMatrix });
  };

  /**
   * Tombol "Baru" di sidebar Template Custom (brief owner 12 Sep, M1: "tombol
   * baru tidak berfungsi").
   *
   * Ia SELALU berfungsi — tapi modal ini memang sudah terbuka dalam keadaan
   * "form template baru yang kosong", jadi menekan "Baru" menyetel state ke
   * nilai yang identik: nol perubahan DOM, nol toast, nol fokus berpindah,
   * dan tombol simpannya ada ribuan piksel di bawah lipatan. Layar benar-benar
   * tidak bergerak — wajar disimpulkan rusak. Teks kosong di sebelahnya bahkan
   * MENYURUH menekannya ("Klik Baru untuk mulai").
   *
   * Jadi yang diperbaiki bukan "fungsinya", melainkan buktinya: gulir editor
   * ke atas, fokuskan kolom pertama, dan beri satu toast. Ditambah penjaga
   * supaya isian yang belum tersimpan tidak lenyap tanpa peringatan.
   */
  const mulaiTemplateBaru = () => {
    const adaIsian = tplModal && (tplModal.label?.trim() || tplModal.key?.trim()
                                  || tplModal.description?.trim());
    if (adaIsian && !window.confirm(
      "Kosongkan form dan mulai template baru? Isian yang belum disimpan akan hilang.")) return;
    openNewTemplate();
    tplEditorRef.current?.scrollTo({ top: 0, behavior: "smooth" });
    // Fokus setelah render supaya kolomnya sudah ada dan sudah kosong.
    setTimeout(() => tplLabelRef.current?.focus(), 0);
    toast.success("Form template baru siap diisi");
  };
  const openEditTemplate = (tpl) => {
    // Normalize permissions to include all modules
    const norm = {};
    modules.forEach(m => {
      const src = tpl.permissions?.[m.key] || {};
      norm[m.key] = { view: !!src.view, create: !!src.create, update: !!src.update, delete: !!src.delete };
    });
    // Preserve built-in keys exactly (biar override menimpa). Custom keys strip "custom_" prefix
    const isBuiltinKey = !tpl.key.startsWith("custom_");
    const rawKey = isBuiltinKey ? tpl.key : tpl.key.replace(/^custom_/, "");
    setTplModal({
      mode: "edit",
      key: rawKey,
      label: tpl.label || "",
      description: tpl.description || "",
      permissions: norm,
      _origKey: tpl.key,
      _isBuiltin: isBuiltinKey,             // untuk lock key field + label save button
      _isOverride: !!tpl.overrides_builtin, // sudah ada override DB
    });
  };
  const toggleTplPerm = (modKey, action, value) => {
    setTplModal(m => m ? ({ ...m, permissions: { ...m.permissions, [modKey]: { ...(m.permissions[modKey] || {}), [action]: value } } }) : m);
  };
  const applyTplPreset = (preset) => {
    setTplModal(m => {
      if (!m) return m;
      const nm = {};
      modules.forEach(mod => {
        if (preset === "all")      nm[mod.key] = { view: true,  create: true,  update: true,  delete: true };
        else if (preset === "read") nm[mod.key] = { view: true, create: false, update: false, delete: false };
        else nm[mod.key] = { view: false, create: false, update: false, delete: false };
      });
      return { ...m, permissions: nm };
    });
  };
  const saveTemplate = async () => {
    if (!tplModal) return;
    if (!tplModal.label.trim()) { toast.error("Label wajib diisi"); return; }
    if (!tplModal.key.trim()) { toast.error("Key wajib diisi (huruf/angka/underscore)"); return; }
    setTplSaving(true);
    try {
      await api.post("/permissions/templates/custom", {
        key: tplModal.key.trim(),
        label: tplModal.label.trim(),
        description: (tplModal.description || "").trim(),
        permissions: tplModal.permissions,
      });
      toast.success(tplModal.mode === "edit" ? "Template diperbarui" : "Template dibuat");
      await reloadTemplates();
      setTplModal(null);
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal simpan"); }
    finally { setTplSaving(false); }
  };
  const deleteTemplate = async (tpl) => {
    const isBuiltin = !tpl.key.startsWith("custom_");
    const hasOverride = !!tpl.overrides_builtin;
    let confirmMsg;
    if (isBuiltin && hasOverride) {
      confirmMsg = `Hapus perubahan pada template "${tpl.label}"? Template akan kembali ke setting BUILT-IN default. Data override kamu akan hilang.`;
    } else if (isBuiltin && !hasOverride) {
      toast.info("Template built-in tanpa perubahan tidak perlu dihapus. Klik Edit untuk buat override dulu.");
      return;
    } else {
      confirmMsg = `Hapus template kustom "${tpl.label}"? Tindakan ini tidak bisa dibatalkan.`;
    }
    if (!confirm(confirmMsg)) return;
    try {
      const r = await api.delete(`/permissions/templates/custom/${tpl.key}`);
      toast.success(r?.data?.reverted_to_builtin ? "Kembali ke built-in default" : "Template dihapus");
      await reloadTemplates();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal hapus"); }
  };

  const applyTemplate = (key) => {
    setSelectedTemplate(key);
    if (!key) return;
    const tpl = permTemplates.find(t => t.key === key);
    if (!tpl) return;
    // Start from "none" then overlay template.permissions (missing modules stay false)
    const base = {};
    modules.forEach(m => { base[m.key] = { view: false, create: false, update: false, delete: false }; });
    Object.entries(tpl.permissions || {}).forEach(([k, actions]) => {
      base[k] = {
        view: !!actions.view, create: !!actions.create,
        update: !!actions.update, delete: !!actions.delete,
      };
      // Bawa `scope` template kalau modulnya scoped DAN nilainya salah satu
      // yang dikenal ("own"/"cabang"/"all") — tanpa ini, preset seperti
      // "Manager Cabang" yang eksplisit menyetel scope:"cabang" di deps.py
      // akan diam-diam kehilangan setelan itu begitu di-apply lewat form ini
      // (cuma view/create/update/delete yang tersalin), dan pengguna harus
      // menyetel ulang manual satu-satu — gampang terlewat.
      if (scopedSet.has(k) && ["own", "cabang", "all"].includes(actions.scope)) {
        base[k].scope = actions.scope;
      }
    });
    setPermMatrix(base);
    toast.success(`Template "${tpl.label}" diterapkan — silakan simpan`);
  };

  const savePerm = async () => {
    if (!permModal) return;
    setPermSaving(true);
    try {
      const payload = { permissions: permMatrix };
      // Only send dashboard_widgets if user configured it explicitly (array)
      if (Array.isArray(permWidgets)) payload.dashboard_widgets = permWidgets;
      // Template yang DITERAPKAN di modal ini direkam sebagai jabatan akun — itulah yang
      // menentukan halaman awal (G8 butir b). Tanpa ini, admin yang dipindah ke template
      // Marketing tetap membawa template lama dan tetap mendarat di modul lama. Kalau
      // tidak ada template yang dipilih, medannya TIDAK dikirim: mengubah centang manual
      // tidak memindah halaman awal (disengaja, terlihat di "Template tercatat").
      if (selectedTemplate) payload.template_key = selectedTemplate;
      await api.patch(`/users/${permModal.user.id}/permissions`, payload);
      // Cabang gudang disimpan lewat PATCH /users/{id} (field `allowed_branches`,
      // [] = cabut pembatasan) — endpoint terpisah dari matriks izin.
      const berubah = JSON.stringify([...permBranches].sort()) !== JSON.stringify([...permBranchesAwal].sort());
      if (berubah) await api.patch(`/users/${permModal.user.id}`, { allowed_branches: permBranches });
      toast.success("Izin tersimpan");
      setPermModal(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal simpan izin");
    } finally { setPermSaving(false); }
  };

  const resetToRoleDefault = () => {
    if (!confirm("Reset izin ke default role? Semua override manual akan hilang.")) return;
    setPermSaving(true);
    // template_key: null — reset ke default role berarti akun tidak lagi memegang jabatan
    // template mana pun, jadi halaman awalnya ikut kembali ke aturan tanpa template.
    api.patch(`/users/${permModal.user.id}/permissions`, { permissions: {}, dashboard_widgets: [], template_key: null })
      .then(() => { toast.success("Reset ke default role"); setPermModal(null); load(); })
      .catch(e => toast.error(e?.response?.data?.detail || "Gagal"))
      .finally(() => setPermSaving(false));
  };

  // Kedua modal ini di JSX komponen halaman yang sama (tidak unmount saat
  // ditutup) — `active` di-set ke keadaan buka masing-masing supaya dirty-state
  // di-reset tiap kali modal dibuka lagi untuk user/template lain. Matrix
  // 71 modul x 4 aksi (dan editor template) mahal diketik ulang dari nol
  // (CLAUDE.md daftar "Modal BERBAHAYA di berkasmu").
  const permGuard = useModalGuard({
    active: !!permModal, dangerous: true, busy: permSaving,
    onClose: () => setPermModal(null), onSubmit: savePerm,
  });
  const tplGuard = useModalGuard({
    active: !!tplModal, dangerous: true, busy: tplSaving,
    onClose: () => setTplModal(null), onSubmit: saveTemplate,
  });

  // Count grants for badge
  const countGrants = (u) => {
    const p = u.permissions || {};
    let total = 0;
    Object.values(p).forEach(actions => {
      Object.values(actions || {}).forEach(v => { if (v) total++; });
    });
    return total;
  };

  // Urutan bawaan (backend: urut nama) dipertahankan sampai header diklik.
  // TIDAK dipakai untuk matrix izin (Fragment dikelompokkan per kategori
  // modul — mengurutkannya merusak pengelompokan itu).
  const { sortKey: userSortKey, sortDir: userSortDir, toggle: toggleUserSort } = useSortState();
  const sortedUsers = sortRows(items, userSortKey, userSortDir, { grants: countGrants });

  return (
    <div className="p-4 md:p-8 max-w-[1400px]">
      <header className="flex items-center justify-between mb-8 flex-wrap gap-3">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wider text-emerald-600 mb-2">Access Control · RBAC</p>
          <h1 className="font-display text-2xl md:text-3xl font-bold text-slate-900">User Management</h1>
          <p className="mt-2 text-sm text-slate-500 max-w-2xl">
            <Info className="w-3.5 h-3.5 inline mb-0.5 mr-1 text-slate-400" />
            Setiap user bisa punya izin granular per fitur (Lihat / Input / Edit / Hapus). Klik ikon <ShieldCheck className="w-3.5 h-3.5 inline text-violet-600" /> untuk atur matrix.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {isSuperadmin && (
            <button onClick={openNewTemplate} data-testid="btn-manage-perm-templates"
              className="inline-flex items-center gap-2 h-10 px-4 rounded-lg border border-violet-300 bg-white hover:bg-violet-50 text-violet-800 text-sm font-medium">
              <LayoutTemplate className="w-4 h-4" /> Kelola Template
              {customTemplates.length > 0 && (
                <span className="ml-1 px-1.5 py-0.5 rounded-full bg-violet-100 text-violet-800 text-[10px] font-bold">
                  {customTemplates.length}
                </span>
              )}
            </button>
          )}
        </div>
      </header>

      <div className="mb-5 bg-emerald-50 border border-emerald-200 rounded-xl p-4 text-sm text-emerald-900 flex items-start gap-3" data-testid="users-info-banner">
        <Info className="w-4 h-4 text-emerald-700 mt-0.5 shrink-0" />
        <div>
          <p className="font-semibold">Alur baru: akun login dibuat lewat halaman <b>Data Karyawan</b>.</p>
          <p className="text-emerald-800 text-xs mt-1">
            Saat HR menambah karyawan, akun login otomatis dibuat berdasarkan email & NIK.
            Halaman ini kini fokus untuk <b>set role &amp; hak akses</b> per user yang sudah ada — klik ikon <ShieldCheck className="w-3.5 h-3.5 inline text-violet-600" /> di baris user.
          </p>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-slate-50 text-xs font-semibold text-slate-500 uppercase tracking-wider">
              <SortTh label="Nama" sortKeyName="name" sortKey={userSortKey} sortDir={userSortDir} onSort={toggleUserSort} testid="users-sort-nama" />
              <SortTh label="Email" sortKeyName="email" sortKey={userSortKey} sortDir={userSortDir} onSort={toggleUserSort} testid="users-sort-email" />
              <SortTh label="Role" sortKeyName="role" sortKey={userSortKey} sortDir={userSortDir} onSort={toggleUserSort} testid="users-sort-role" />
              <SortTh label="Izin Kustom" sortKeyName="grants" sortKey={userSortKey} sortDir={userSortDir} onSort={toggleUserSort} testid="users-sort-izin" />
              <th className="text-right py-3 px-4"></th>
            </tr>
          </thead>
          <tbody>
            {sortedUsers.map(u => {
              const grants = countGrants(u);
              const hasOverride = grants > 0;
              return (
                <tr key={u.id} className="border-b border-slate-100 hover:bg-slate-50 transition-colors" data-testid={`user-row-${u.id}`}>
                  <td className="py-3 px-4 font-medium text-slate-900">{u.name}</td>
                  <td className="py-3 px-4 text-slate-600">{u.email}</td>
                  <td className="py-3 px-4">
                    <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${roleColor(u.role)}`}>
                      {u.role}
                    </span>
                  </td>
                  <td className="py-3 px-4">
                    {u.role === "superadmin" ? (
                      <span className="text-xs text-emerald-700 inline-flex items-center gap-1">
                        <Sparkles className="w-3.5 h-3.5" /> Bypass — akses penuh
                      </span>
                    ) : hasOverride ? (
                      <span className="text-xs px-2 py-0.5 rounded-full bg-violet-100 text-violet-800 font-medium inline-flex items-center gap-1"
                        data-testid={`user-perm-count-${u.id}`}>
                        <ShieldCheck className="w-3 h-3" /> {grants} izin kustom
                      </span>
                    ) : (
                      <span className="text-xs text-slate-400 italic">— default role —</span>
                    )}
                  </td>
                  <td className="py-3 px-4 text-right">
                    <div className="inline-flex gap-1">
                      {u.role !== "superadmin" && (
                        <button onClick={() => openPermEditor(u)} data-testid={`btn-edit-perm-${u.id}`}
                          title="Atur izin per-modul"
                          className="p-2 rounded-lg text-slate-500 hover:bg-violet-50 hover:text-violet-700">
                          <ShieldCheck className="w-4 h-4" />
                        </button>
                      )}
                      {isSuperadmin && u.role !== "superadmin" && (
                        <button onClick={() => bukaResetPassword(u)} data-testid={`btn-reset-pw-${u.id}`}
                          title="Ganti password pengguna ini — password lama tidak pernah bisa dilihat"
                          className="p-2 rounded-lg text-slate-500 hover:bg-amber-50 hover:text-amber-700">
                          <KeyRound className="w-4 h-4" />
                        </button>
                      )}
                      {u.id !== user.id && (
                        <button onClick={() => del(u.id)} data-testid={`btn-del-user-${u.id}`}
                          className="p-2 rounded-lg text-slate-400 hover:bg-rose-50 hover:text-rose-600">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
            {items.length === 0 && <tr><td colSpan={5} className="text-center py-10 text-sm text-slate-500">
              <UserCog className="w-8 h-8 text-slate-300 mx-auto mb-2" />Belum ada user
            </td></tr>}
          </tbody>
        </table>
      </div>

      {/* Create User Modal */}
      {modal && (
        <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm grid place-items-center p-4" onClick={() => setModal(false)}>
          <div className="bg-white rounded-2xl w-full max-w-md" onClick={e => e.stopPropagation()}>
            <div className="border-b border-slate-200 px-6 py-4 flex items-center justify-between">
              <h3 className="font-display font-semibold text-slate-900">Tambah User</h3>
              <button onClick={() => setModal(false)} className="p-2 hover:bg-slate-100 rounded-lg"><X className="w-4 h-4" /></button>
            </div>
            <div className="p-6 space-y-4">
              <label className="block"><span className="text-xs font-medium text-slate-600">Nama</span>
                <input value={form.name} onChange={e => setForm(s => ({ ...s, name: e.target.value }))} data-testid="user-name"
                  className="mt-1 w-full h-10 px-3 rounded-lg border border-slate-200 text-sm" /></label>
              <label className="block"><span className="text-xs font-medium text-slate-600">Email</span>
                <input type="email" value={form.email} onChange={e => setForm(s => ({ ...s, email: e.target.value }))} data-testid="user-email"
                  className="mt-1 w-full h-10 px-3 rounded-lg border border-slate-200 text-sm" /></label>
              <label className="block"><span className="text-xs font-medium text-slate-600">Password</span>
                <input type="password" value={form.password} onChange={e => setForm(s => ({ ...s, password: e.target.value }))} data-testid="user-password"
                  className="mt-1 w-full h-10 px-3 rounded-lg border border-slate-200 text-sm" /></label>
              <label className="block"><span className="text-xs font-medium text-slate-600">Role Awal</span>
                <select value={form.role} onChange={e => setForm(s => ({ ...s, role: e.target.value }))} data-testid="user-role"
                  className="mt-1 w-full h-10 px-3 rounded-lg border border-slate-200 text-sm bg-white">
                  {ROLES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                </select>
              </label>
              <label className="block">
                <span className="text-xs font-medium text-slate-600 inline-flex items-center gap-1">
                  <LayoutTemplate className="w-3.5 h-3.5 text-violet-600" /> Template Hak Akses (opsional)
                </span>
                <select value={form.template_key || ""}
                  onChange={e => setForm(s => ({ ...s, template_key: e.target.value, role: e.target.value ? "custom" : s.role }))}
                  data-testid="user-template"
                  className="mt-1 w-full h-10 px-3 rounded-lg border border-slate-200 text-sm bg-white">
                  <option value="">— Tidak pakai template (pakai default role) —</option>
                  {permTemplates.map(t => (
                    <option key={t.key} value={t.key}>{t.is_custom ? "★ " : ""}{t.label}</option>
                  ))}
                </select>
                <p className="text-[11px] text-slate-500 mt-1">
                  Pilih template → hak akses otomatis diterapkan. Bisa disesuaikan lagi via ikon <ShieldCheck className="w-3 h-3 inline text-violet-600" />.
                  {isSuperadmin && <> · Butuh template baru? Klik <b>Kelola Template</b> di header.</>}
                </p>
              </label>
              <div className="flex justify-end gap-2">
                <button onClick={() => setModal(false)} className="h-10 px-4 rounded-lg border border-slate-200 text-sm">Batal</button>
                <button onClick={save} data-testid="btn-save-user" className="h-10 px-5 rounded-lg bg-[#059669] hover:bg-[#047857] text-white text-sm font-medium">Simpan</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Reset Password Modal (poin 5 owner) — password lama TIDAK PERNAH bisa
          dilihat (hash bcrypt satu arah), hanya bisa DIGANTI oleh superadmin. */}
      {pwModal && (
        <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm grid place-items-center p-4" onClick={() => setPwModal(null)}>
          <div className="bg-white rounded-2xl w-full max-w-md" onClick={e => e.stopPropagation()} data-testid="reset-pw-modal">
            <div className="border-b border-slate-200 px-6 py-4 flex items-center justify-between">
              <div>
                <h3 className="font-display font-semibold text-slate-900 inline-flex items-center gap-1.5">
                  <KeyRound className="w-4 h-4 text-amber-600" /> Ganti Password
                </h3>
                <p className="text-xs text-slate-500 mt-0.5">{pwModal.name} · {pwModal.email}</p>
              </div>
              <button onClick={() => setPwModal(null)} className="p-2 hover:bg-slate-100 rounded-lg"><X className="w-4 h-4" /></button>
            </div>
            <div className="p-6 space-y-4">
              <p className="text-xs text-slate-500 bg-amber-50 border border-amber-200 rounded-lg p-3">
                Password lama tidak bisa ditampilkan (tersimpan terenkripsi satu arah) — hanya bisa diganti
                dengan password baru. Pengguna ini harus memakai password baru pada login berikutnya.
              </p>
              <label className="block"><span className="text-xs font-medium text-slate-600">Password Baru</span>
                <input type="password" value={pwForm.new_password} data-testid="reset-pw-new"
                  placeholder="minimal 6 karakter"
                  onChange={e => setPwForm(s => ({ ...s, new_password: e.target.value }))}
                  className="mt-1 w-full h-10 px-3 rounded-lg border border-slate-200 text-sm" /></label>
              <label className="block"><span className="text-xs font-medium text-slate-600">Ulangi Password Baru</span>
                <input type="password" value={pwForm.confirm} data-testid="reset-pw-confirm"
                  onChange={e => setPwForm(s => ({ ...s, confirm: e.target.value }))}
                  className="mt-1 w-full h-10 px-3 rounded-lg border border-slate-200 text-sm" /></label>
              <div className="flex justify-end gap-2">
                <button onClick={() => setPwModal(null)} className="h-10 px-4 rounded-lg border border-slate-200 text-sm">Batal</button>
                <button onClick={submitResetPassword} disabled={pwSaving} data-testid="reset-pw-submit"
                  className="h-10 px-5 rounded-lg bg-amber-600 hover:bg-amber-700 text-white text-sm font-medium disabled:opacity-50">
                  {pwSaving ? "Menyimpan..." : "Ganti Password"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Permission Matrix Modal */}
      {permModal && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm grid place-items-center p-4 overflow-y-auto"
          onClick={permGuard.requestClose}>
          <div ref={permGuard.contentRef} className="bg-white rounded-2xl w-full max-w-4xl my-8 shadow-2xl" onClick={e => e.stopPropagation()}
            data-testid="perm-matrix-modal">
            <div className="sticky top-0 bg-gradient-to-r from-violet-600 to-violet-500 text-white px-6 py-4 flex items-center justify-between rounded-t-2xl z-10">
              <div className="flex items-center gap-3">
                <ShieldCheck className="w-6 h-6" />
                <div>
                  <h3 className="font-display font-semibold text-base">Atur Izin — {permModal.user.name}</h3>
                  <p className="text-[11px] text-violet-100">{permModal.user.email} · role: <b>{permModal.user.role}</b> {permModal.has_override && "· sudah punya override"}</p>
                  {/* Template yang TERSIMPAN, bukan pilihan di dropdown di bawah: inilah
                      yang menentukan halaman awal akun (G8 butir b). Ditampilkan supaya
                      jelas kenapa mengubah centang manual tidak memindah halaman awal. */}
                  <p className="text-[11px] text-violet-100" data-testid="perm-template-tercatat">
                    Template tercatat (menentukan halaman awal):{" "}
                    <b>{permModal.user.template_key
                      ? (permTemplates.find(t => t.key === permModal.user.template_key)?.label || permModal.user.template_key)
                      : "—"}</b>
                  </p>
                </div>
              </div>
              <button onClick={permGuard.requestClose}
                className="p-2 hover:bg-white/20 rounded-lg" data-testid="perm-modal-close">
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="p-5">
              {/* Template selector row */}
              <div className="bg-gradient-to-r from-violet-50 to-fuchsia-50 border border-violet-200 rounded-lg p-3 mb-4 flex flex-wrap items-center gap-2">
                <LayoutTemplate className="w-4 h-4 text-violet-700" />
                <span className="text-xs font-semibold text-violet-900">Template Divisi:</span>
                <select value={selectedTemplate}
                  onChange={(e) => applyTemplate(e.target.value)}
                  data-testid="perm-template-select"
                  className="h-8 px-3 rounded-md border border-violet-300 text-xs bg-white min-w-[220px]">
                  <option value="">— Pilih preset per divisi —</option>
                  {permTemplates.map(t => (
                    <option key={t.key} value={t.key}>{t.is_custom ? "★ " : ""}{t.label}</option>
                  ))}
                </select>
                {selectedTemplate && (
                  <span className="text-[11px] text-violet-700 italic">
                    {permTemplates.find(t => t.key === selectedTemplate)?.description}
                  </span>
                )}
              </div>

              {/* Cabang gudang yang boleh diakses (brief 4 Sep 2026 P16). Berlaku untuk
                  gudang/unit/laporan stok/request stok lewat allowed_warehouse_ids
                  di backend — bukan lingkup "cabang" matriks izin (itu dimensi
                  karyawan). */}
              <div className="mb-4 border border-emerald-200 rounded-lg bg-emerald-50/40" data-testid="perm-cabang-gudang">
                <div className="flex items-center gap-2 px-3 py-2 border-b border-emerald-200 bg-emerald-50 rounded-t-lg">
                  <span className="text-xs font-semibold text-emerald-900">Cabang gudang yang boleh diakses</span>
                  <span className="text-[10px] text-emerald-700 italic">kosong = semua gudang; berlaku untuk stok, unit, laporan, dan request stok; role admin/superadmin/finance_ceo tidak pernah dibatasi</span>
                </div>
                <div className="px-3 py-2 flex flex-wrap gap-2">
                  {cabangOptions.map(c => (
                    <label key={c} className="inline-flex items-center gap-1.5 text-xs text-slate-700 cursor-pointer">
                      <input type="checkbox" checked={permBranches.includes(c)} data-testid={`perm-cabang-${c}`}
                        onChange={e => setPermBranches(b => e.target.checked ? [...b, c] : b.filter(x => x !== c))}
                        className="w-3.5 h-3.5 rounded border-slate-300" />
                      {c}
                    </label>
                  ))}
                  {permBranches.filter(c => !cabangOptions.includes(c)).map(c => (
                    <label key={c} className="inline-flex items-center gap-1.5 text-xs text-amber-700 cursor-pointer" title="Ejaan cabang lama — cabut kalau tidak dikenal">
                      <input type="checkbox" checked onChange={() => setPermBranches(b => b.filter(x => x !== c))} className="w-3.5 h-3.5 rounded border-amber-300" />
                      {c}
                    </label>
                  ))}
                </div>
              </div>

              {/* Izin Sementara / Berjangka Waktu (poin 13 owner) — "beri akses edit
                  faktur 5 menit, lewat itu terkunci otomatis". Berlaku SEKETIKA,
                  terpisah dari form matrix di bawah supaya tidak perlu tekan
                  "Simpan Izin" dan tidak bisa tertimpa tanpa sengaja. */}
              <div className="mb-4 border border-amber-200 rounded-lg bg-amber-50/50" data-testid="temp-grant-panel">
                <div className="flex items-center gap-2 px-3 py-2 border-b border-amber-200 bg-amber-50 rounded-t-lg">
                  <Timer className="w-4 h-4 text-amber-700" />
                  <span className="text-xs font-semibold text-amber-900">Izin Sementara (Berjangka Waktu)</span>
                  <span className="text-[10px] text-amber-700 italic">berlaku seketika, otomatis terkunci saat waktu habis</span>
                </div>
                <div className="p-3 flex flex-wrap items-end gap-3">
                  <label className="block">
                    <span className="text-[10px] font-medium text-slate-600">Modul</span>
                    <select value={tempForm.module}
                      onChange={e => setTempForm(f => ({ ...f, module: e.target.value }))}
                      data-testid="temp-grant-module"
                      className="mt-1 h-8 px-2 rounded-md border border-slate-200 text-xs bg-white min-w-[200px]">
                      <option value="">— pilih modul —</option>
                      {modules.map(m => <option key={m.key} value={m.key}>{m.label}</option>)}
                    </select>
                  </label>
                  <div>
                    <span className="block text-[10px] font-medium text-slate-600 mb-1.5">Aksi (bisa multi-pilih)</span>
                    <div className="flex gap-2.5">
                      {ACTIONS.map(ac => (
                        <label key={ac.key} className="inline-flex items-center gap-1 text-xs cursor-pointer">
                          <input type="checkbox" checked={tempForm.actions.includes(ac.key)}
                            onChange={() => toggleTempAction(ac.key)}
                            data-testid={`temp-grant-action-${ac.key}`}
                            className={`w-3.5 h-3.5 rounded accent-${ac.color}-600 cursor-pointer`} />
                          <span className={`text-${ac.color}-700`}>{ac.label}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                  <label className="block">
                    <span className="text-[10px] font-medium text-slate-600">Durasi</span>
                    <select value={tempForm.duration}
                      onChange={e => setTempForm(f => ({ ...f, duration: e.target.value }))}
                      data-testid="temp-grant-duration"
                      className="mt-1 h-8 px-2 rounded-md border border-slate-200 text-xs bg-white">
                      {TEMP_DURATIONS.map(d => <option key={d.value} value={d.value}>{d.label}</option>)}
                    </select>
                  </label>
                  <button onClick={submitTempGrant} disabled={tempSaving} data-testid="btn-grant-temp-perm"
                    className="h-8 px-3 rounded-md bg-amber-600 hover:bg-amber-700 text-white text-xs font-medium disabled:opacity-50 inline-flex items-center gap-1.5">
                    <Clock className="w-3.5 h-3.5" /> {tempSaving ? "Menyimpan..." : "Beri Izin Sementara"}
                  </button>
                </div>
                {timeBoxedEntries.length > 0 && (
                  <div className="px-3 pb-3">
                    <p className="text-[10px] font-semibold text-amber-800 mb-1.5">Izin berjangka waktu tercatat untuk user ini:</p>
                    <div className="flex flex-wrap gap-1.5">
                      {timeBoxedEntries.map(g => {
                        const sisa = formatSisaWaktu(g.expiresAt, nowTick);
                        const habis = sisa === "Kedaluwarsa";
                        const modLabel = modules.find(m => m.key === g.modKey)?.label || g.modKey;
                        const acLabel = ACTIONS.find(a => a.key === g.action)?.label || g.action;
                        return (
                          <span key={`${g.modKey}-${g.action}`}
                            data-testid={`temp-grant-badge-${g.modKey}-${g.action}`}
                            className={`inline-flex items-center gap-1.5 text-[10px] pl-2 pr-1 py-1 rounded-full border ${
                              habis ? "bg-rose-50 border-rose-300 text-rose-700" : "bg-white border-amber-300 text-amber-800"
                            }`}>
                            <Clock className="w-3 h-3" /> {modLabel} · {acLabel} — {sisa}
                            <button onClick={() => jadikanPermanen(g.modKey, g.action)}
                              data-testid={`btn-make-permanent-${g.modKey}-${g.action}`}
                              title="Jadikan permanen (cabut batas waktu)"
                              className="px-1.5 py-0.5 rounded-full bg-emerald-100 hover:bg-emerald-200 text-emerald-800 font-semibold">
                              permanenkan
                            </button>
                          </span>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>

              {/* Preset row */}
              <div className="flex items-center gap-2 mb-4 flex-wrap bg-slate-50 border border-slate-200 rounded-lg p-2.5">
                <span className="text-xs font-semibold text-slate-600 mr-1">Preset cepat:</span>
                <button onClick={() => applyPreset("all")} data-testid="perm-preset-all"
                  className="text-xs px-3 py-1 rounded-md bg-emerald-100 text-emerald-800 hover:bg-emerald-200 font-medium">
                  Semua Aktif
                </button>
                <button onClick={() => applyPreset("read")} data-testid="perm-preset-read"
                  className="text-xs px-3 py-1 rounded-md bg-sky-100 text-sky-800 hover:bg-sky-200 font-medium">
                  Hanya Lihat (Read-only)
                </button>
                <button onClick={() => applyPreset("none")} data-testid="perm-preset-none"
                  className="text-xs px-3 py-1 rounded-md bg-rose-100 text-rose-800 hover:bg-rose-200 font-medium">
                  Semua Non-aktif
                </button>
                <span className="ml-auto text-[11px] text-slate-500 italic">Toggle per baris/kolom via header</span>
              </div>

              {/* Matrix table */}
              <div className="border border-slate-200 rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-slate-100 text-xs font-semibold text-slate-700">
                      <th className="text-left py-2.5 px-3">Fitur / Modul</th>
                      {ACTIONS.map(ac => (
                        <th key={ac.key} className="text-center py-2.5 px-3 w-24">
                          <div className="flex flex-col items-center gap-1">
                            <span className={`text-${ac.color}-700`}>{ac.label}</span>
                            <div className="flex gap-1">
                              <button onClick={() => toggleActionAll(ac.key, true)}
                                title={`Semua modul → ${ac.label} ON`}
                                data-testid={`perm-col-on-${ac.key}`}
                                className="w-4 h-4 rounded bg-emerald-500 hover:bg-emerald-600 text-white text-[9px] font-bold">✓</button>
                              <button onClick={() => toggleActionAll(ac.key, false)}
                                title={`Semua modul → ${ac.label} OFF`}
                                data-testid={`perm-col-off-${ac.key}`}
                                className="w-4 h-4 rounded bg-slate-300 hover:bg-slate-400 text-white text-[9px] font-bold">✕</button>
                            </div>
                          </div>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(grouped).map(([group, mods]) => (
                      <Fragment key={`grp-${group}`}>
                        <tr>
                          <td colSpan={5} className="bg-slate-50 py-1.5 px-3 text-[11px] font-bold uppercase tracking-wider text-slate-500">
                            {group}
                          </td>
                        </tr>
                        {mods.map(m => {
                          const row = permMatrix[m.key] || {};
                          const allOn = ACTIONS.every(a => row[a.key]);
                          const isScoped = scopedSet.has(m.key);
                          const curScope = row.scope || "own";
                          return (
                            <tr key={m.key} className="border-t border-slate-100 hover:bg-violet-50/30">
                              <td className="py-2 px-3">
                                <div className="flex items-center justify-between gap-2">
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2">
                                      <span className="font-medium text-slate-800">{m.label}</span>
                                      {isScoped && (
                                        <select
                                          value={curScope}
                                          onChange={e => setScope(m.key, e.target.value)}
                                          data-testid={`perm-scope-${m.key}`}
                                          title="Cakupan data: data sendiri, hanya cabang sendiri, atau semua cabang"
                                          className="text-[10px] px-1.5 py-0.5 rounded border border-emerald-300 bg-emerald-50 text-emerald-800 font-semibold cursor-pointer">
                                          <option value="own">Data Sendiri</option>
                                          <option value="cabang">Hanya Cabang Saya</option>
                                          <option value="all">Semua Cabang</option>
                                        </select>
                                      )}
                                    </div>
                                  </div>
                                  <button onClick={() => toggleModuleAll(m.key, !allOn)}
                                    title={allOn ? "Matikan semua aksi baris ini" : "Aktifkan semua aksi baris ini"}
                                    data-testid={`perm-row-toggle-${m.key}`}
                                    className={`text-[9px] px-1.5 py-0.5 rounded font-bold shrink-0 ${allOn ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-600"}`}>
                                    {allOn ? "ALL" : "SET"}
                                  </button>
                                </div>
                              </td>
                              {ACTIONS.map(ac => {
                                const expiryTs = row[ac.key] && row.expires?.[ac.key];
                                return (
                                <td key={ac.key} className="text-center py-2 px-3">
                                  <label className="inline-flex items-center cursor-pointer">
                                    <input type="checkbox" checked={!!row[ac.key]}
                                      onChange={e => togglePerm(m.key, ac.key, e.target.checked)}
                                      data-testid={`perm-${m.key}-${ac.key}`}
                                      className={`w-4 h-4 rounded accent-${ac.color}-600 cursor-pointer`} />
                                  </label>
                                  {/* Izin berjangka waktu (poin 13): tandai LANGSUNG di kotaknya
                                      supaya admin tidak perlu menggulir ke panel ringkasan untuk
                                      sadar bahwa centang ini akan terkunci sendiri. */}
                                  {expiryTs && (
                                    <div className="mt-0.5" data-testid={`perm-expiry-${m.key}-${ac.key}`}
                                      title={`Kedaluwarsa: ${new Date(expiryTs).toLocaleString("id-ID")}`}>
                                      <span className={`text-[8px] font-semibold inline-flex items-center gap-0.5 ${
                                        formatSisaWaktu(expiryTs, nowTick) === "Kedaluwarsa" ? "text-rose-600" : "text-amber-600"
                                      }`}>
                                        <Clock className="w-2.5 h-2.5" /> {formatSisaWaktu(expiryTs, nowTick)}
                                      </span>
                                    </div>
                                  )}
                                </td>
                                );
                              })}
                            </tr>
                          );
                        })}
                      </Fragment>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Dashboard Widget Selector */}
              {dashboardWidgets.length > 0 && (
                <div className="mt-5 border border-emerald-200 rounded-lg bg-emerald-50/40" data-testid="widget-selector-panel">
                  <div className="flex items-center justify-between gap-2 px-4 py-2.5 border-b border-emerald-200 bg-emerald-50">
                    <div className="flex items-center gap-2">
                      <LayoutDashboard className="w-4 h-4 text-emerald-700" />
                      <span className="text-sm font-semibold text-emerald-900">Widget Dashboard yang Terlihat</span>
                      <span className="text-[10px] text-emerald-700 italic">
                        ({Array.isArray(permWidgets) ? `${permWidgets.length}/${dashboardWidgets.length} terpilih` : "default: semua tampil"})
                      </span>
                    </div>
                    <div className="flex gap-1">
                      <button onClick={() => toggleAllWidgets(true)} data-testid="widget-select-all"
                        className="text-[10px] px-2 py-1 rounded bg-emerald-600 hover:bg-emerald-700 text-white font-medium">
                        Pilih Semua
                      </button>
                      <button onClick={() => toggleAllWidgets(false)} data-testid="widget-select-none"
                        className="text-[10px] px-2 py-1 rounded bg-slate-200 hover:bg-slate-300 text-slate-700 font-medium">
                        Kosongkan
                      </button>
                      <button onClick={resetWidgetsToDefault} data-testid="widget-reset-default"
                        className="text-[10px] px-2 py-1 rounded border border-slate-300 bg-white hover:bg-slate-50 text-slate-700 font-medium">
                        Default
                      </button>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-1.5 p-3">
                    {dashboardWidgets.map(w => {
                      const checked = Array.isArray(permWidgets) ? permWidgets.includes(w.key) : true;
                      return (
                        <label key={w.key} className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-white cursor-pointer text-xs">
                          <input type="checkbox" checked={checked}
                            onChange={() => toggleWidget(w.key)}
                            data-testid={`widget-cb-${w.key}`}
                            className="w-3.5 h-3.5 rounded accent-emerald-600 cursor-pointer" />
                          <span className="text-slate-700">{w.label}</span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Footer actions */}
              <div className="mt-4 pt-4 border-t border-slate-100 flex flex-wrap items-center justify-between gap-2">
                <button onClick={resetToRoleDefault} disabled={permSaving}
                  data-testid="btn-reset-perm"
                  className="inline-flex items-center gap-1.5 h-10 px-3 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 text-sm font-medium disabled:opacity-50">
                  <RotateCcw className="w-3.5 h-3.5" /> Reset ke Default Role
                </button>
                <div className="flex gap-2">
                  <button onClick={permGuard.requestClose}
                    className="h-10 px-4 rounded-lg border border-slate-200 text-sm">Batal</button>
                  <button onClick={savePerm} disabled={permSaving}
                    data-testid="btn-save-perm"
                    className="h-10 px-5 rounded-lg bg-violet-600 hover:bg-violet-700 text-white text-sm font-medium inline-flex items-center gap-1.5 disabled:opacity-50">
                    <Save className="w-4 h-4" /> {permSaving ? "Menyimpan..." : "Simpan Izin"}
                  </button>
                </div>
              </div>
              <p className="mt-3 text-[11px] text-slate-500 italic">
                <KeyRound className="w-3 h-3 inline mb-0.5" /> Perubahan matrix di atas berlaku setelah <b>Simpan Izin</b>. Izin
                Sementara (panel kuning) berlaku SEKETIKA tanpa perlu Simpan, dan otomatis terkunci sendiri saat waktunya habis —
                sisi lain menu bisa butuh logout &amp; login ulang untuk melihat efeknya di sidebar.
              </p>
            </div>
            {permGuard.confirming && (
              <UnsavedGuardBar onStay={permGuard.cancelClose} onDiscard={permGuard.confirmDiscard} testid="perm-modal-guard-bar" />
            )}
          </div>
        </div>
      )}

      {/* Custom Template CRUD Modal (Superadmin only) */}
      {tplModal && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm grid place-items-center p-4 overflow-y-auto"
          onClick={tplGuard.requestClose}>
          <div ref={tplGuard.contentRef} className="bg-white rounded-2xl w-full max-w-5xl my-8 shadow-2xl" onClick={e => e.stopPropagation()}
            data-testid="tpl-modal">
            <div className="sticky top-0 bg-gradient-to-r from-violet-700 to-fuchsia-600 text-white px-6 py-4 flex items-center justify-between rounded-t-2xl z-10">
              <div className="flex items-center gap-3">
                <LayoutTemplate className="w-6 h-6" />
                <div>
                  <h3 className="font-display font-semibold text-base">Kelola Template Izin Custom</h3>
                  <p className="text-[11px] text-violet-100">
                    {tplModal.mode === "new" ? "Buat template baru untuk divisi/role khusus" : `Edit: ${tplModal.label}`}
                    {" · "}Template custom tampil di dropdown &ldquo;Template Divisi&rdquo;
                  </p>
                </div>
              </div>
              <button onClick={tplGuard.requestClose} className="p-2 hover:bg-white/20 rounded-lg" data-testid="tpl-modal-close">
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-[280px,1fr] gap-0">
              {/* Sidebar: existing custom templates */}
              <div className="border-r border-slate-200 bg-slate-50 p-4 max-h-[70vh] overflow-y-auto">
                <div className="flex items-center justify-between mb-3">
                  <p className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Template Custom</p>
                  <button onClick={mulaiTemplateBaru} data-testid="btn-new-tpl"
                    className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded bg-violet-600 hover:bg-violet-700 text-white font-medium">
                    <FilePlus2 className="w-3 h-3" /> Baru
                  </button>
                </div>
                {customTemplates.length === 0 && (
                  <p className="text-[11px] text-slate-400 italic text-center py-6">
                    Belum ada template custom. Klik <b>Baru</b> untuk mulai.
                  </p>
                )}
                <div className="space-y-1.5">
                  {customTemplates.map(t => (
                    <div key={t.key} data-testid={`tpl-item-${t.key}`}
                      className={`rounded-lg border p-2.5 cursor-pointer transition-colors ${
                        tplModal._origKey === t.key ? "border-violet-500 bg-white shadow-sm" : "border-slate-200 bg-white hover:border-violet-300"
                      }`}
                      onClick={() => openEditTemplate(t)}>
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0 flex-1">
                          <p className="text-xs font-semibold text-slate-900 truncate">{t.label}</p>
                          <p className="text-[10px] text-slate-500 font-mono truncate">{t.key}</p>
                          {t.description && <p className="text-[10px] text-slate-500 italic mt-1 line-clamp-2">{t.description}</p>}
                        </div>
                        <div className="flex flex-col gap-1">
                          <button onClick={(e) => { e.stopPropagation(); openEditTemplate(t); }}
                            data-testid={`btn-edit-tpl-${t.key}`}
                            className="p-1 rounded hover:bg-violet-50 text-slate-500 hover:text-violet-700">
                            <Pencil className="w-3 h-3" />
                          </button>
                          <button onClick={(e) => { e.stopPropagation(); deleteTemplate(t); }}
                            data-testid={`btn-del-tpl-${t.key}`}
                            className="p-1 rounded hover:bg-rose-50 text-slate-400 hover:text-rose-600">
                            <Trash2 className="w-3 h-3" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="mt-4 pt-3 border-t border-slate-200">
                  <p className="text-[10px] text-slate-500 mb-2 flex items-center gap-1">
                    <FileText className="w-3 h-3" /> Built-in ({permTemplates.filter(t => !t.key.startsWith("custom_")).length})
                    <span className="text-[9px] text-slate-400">— klik untuk edit / hapus override</span>
                  </p>
                  <div className="space-y-1">
                    {permTemplates.filter(t => !t.key.startsWith("custom_")).map(t => (
                      <div key={t.key} data-testid={`tpl-item-${t.key}`}
                        className={`rounded-md border p-2 cursor-pointer transition-colors ${
                          tplModal._origKey === t.key ? "border-violet-500 bg-white shadow-sm"
                          : t.overrides_builtin ? "border-amber-200 bg-amber-50/50 hover:border-amber-300"
                          : "border-slate-200 bg-slate-50 hover:border-violet-300 hover:bg-white"
                        }`}
                        onClick={() => openEditTemplate(t)}>
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0 flex-1">
                            <p className="text-[11px] font-semibold text-slate-800 truncate flex items-center gap-1">
                              {t.label}
                              {t.overrides_builtin && (
                                <span className="text-[8px] font-bold px-1 py-0.5 rounded bg-amber-500 text-white uppercase tracking-wider">Modif</span>
                              )}
                            </p>
                            <p className="text-[9px] text-slate-500 font-mono truncate">{t.key}</p>
                          </div>
                          <div className="flex flex-col gap-0.5 shrink-0">
                            <button onClick={(e) => { e.stopPropagation(); openEditTemplate(t); }}
                              data-testid={`btn-edit-tpl-${t.key}`}
                              title="Edit / buat override"
                              className="p-1 rounded hover:bg-violet-50 text-slate-500 hover:text-violet-700">
                              <Pencil className="w-3 h-3" />
                            </button>
                            {t.overrides_builtin && (
                              <button onClick={(e) => { e.stopPropagation(); deleteTemplate(t); }}
                                data-testid={`btn-del-tpl-${t.key}`}
                                title="Kembalikan ke built-in default"
                                className="p-1 rounded hover:bg-rose-50 text-slate-400 hover:text-rose-600">
                                <Trash2 className="w-3 h-3" />
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Editor form */}
              <div ref={tplEditorRef} className="p-5 max-h-[70vh] overflow-y-auto">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
                  <label className="block md:col-span-2">
                    <span className="text-xs font-medium text-slate-600">Label Template *</span>
                    <input value={tplModal.label} data-testid="tpl-label" ref={tplLabelRef} autoFocus
                      onChange={e => setTplModal(m => ({ ...m, label: e.target.value }))}
                      placeholder="mis. Supervisor Gudang"
                      className="mt-1 w-full h-9 px-3 rounded-lg border border-slate-200 text-sm" />
                  </label>
                  <label className="block">
                    <span className="text-xs font-medium text-slate-600">Key (unique) *</span>
                    <input value={tplModal.key} data-testid="tpl-key"
                      onChange={e => setTplModal(m => ({ ...m, key: e.target.value.replace(/[^a-z0-9_]/gi, "_").toLowerCase() }))}
                      placeholder="supervisor_gudang" disabled={tplModal.mode === "edit"}
                      className="mt-1 w-full h-9 px-3 rounded-lg border border-slate-200 text-sm font-mono disabled:bg-slate-100" />
                  </label>
                  <label className="block md:col-span-3">
                    <span className="text-xs font-medium text-slate-600">Deskripsi</span>
                    <input value={tplModal.description} data-testid="tpl-desc"
                      onChange={e => setTplModal(m => ({ ...m, description: e.target.value }))}
                      placeholder="Deskripsi singkat untuk pengingat"
                      className="mt-1 w-full h-9 px-3 rounded-lg border border-slate-200 text-sm" />
                  </label>
                </div>

                {/* Quick presets */}
                <div className="flex items-center gap-2 mb-3 flex-wrap bg-slate-50 border border-slate-200 rounded-lg p-2.5">
                  <span className="text-xs font-semibold text-slate-600 mr-1">Preset cepat:</span>
                  <button onClick={() => applyTplPreset("all")} data-testid="tpl-preset-all"
                    className="text-xs px-3 py-1 rounded-md bg-emerald-100 text-emerald-800 hover:bg-emerald-200 font-medium">
                    Semua Aktif
                  </button>
                  <button onClick={() => applyTplPreset("read")} data-testid="tpl-preset-read"
                    className="text-xs px-3 py-1 rounded-md bg-sky-100 text-sky-800 hover:bg-sky-200 font-medium">
                    Read-only
                  </button>
                  <button onClick={() => applyTplPreset("none")} data-testid="tpl-preset-none"
                    className="text-xs px-3 py-1 rounded-md bg-rose-100 text-rose-800 hover:bg-rose-200 font-medium">
                    Semua Nonaktif
                  </button>
                </div>

                {/* Matrix */}
                <div className="border border-slate-200 rounded-lg overflow-hidden">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-slate-100 text-xs font-semibold text-slate-700">
                        <th className="text-left py-2 px-3">Fitur</th>
                        {ACTIONS.map(ac => (
                          <th key={ac.key} className={`text-center py-2 px-3 w-20 text-${ac.color}-700`}>{ac.label}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {Object.entries(grouped).map(([group, mods]) => (
                        <Fragment key={`tplg-${group}`}>
                          <tr>
                            <td colSpan={5} className="bg-slate-50 py-1 px-3 text-[11px] font-bold uppercase tracking-wider text-slate-500">
                              {group}
                            </td>
                          </tr>
                          {mods.map(m => {
                            const row = tplModal.permissions[m.key] || {};
                            return (
                              <tr key={`tpl-${m.key}`} className="border-t border-slate-100 hover:bg-violet-50/30">
                                <td className="py-1.5 px-3 font-medium text-slate-800 text-xs">{m.label}</td>
                                {ACTIONS.map(ac => (
                                  <td key={ac.key} className="text-center py-1.5 px-3">
                                    <input type="checkbox" checked={!!row[ac.key]}
                                      onChange={e => toggleTplPerm(m.key, ac.key, e.target.checked)}
                                      data-testid={`tpl-${m.key}-${ac.key}`}
                                      className={`w-4 h-4 rounded accent-${ac.color}-600 cursor-pointer`} />
                                  </td>
                                ))}
                              </tr>
                            );
                          })}
                        </Fragment>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Sticky (brief 12 Sep, M1): matriks izin di atasnya ~2.800 px
                    untuk 94 modul, jadi tombol simpan yang mengalir di ujung
                    praktis tidak pernah terlihat tanpa menggulir sampai habis. */}
                <div className="sticky bottom-0 -mx-5 -mb-5 mt-4 px-5 py-3 bg-white/95 backdrop-blur border-t border-slate-200 flex justify-end gap-2">
                  <button onClick={tplGuard.requestClose}
                    className="h-10 px-4 rounded-lg border border-slate-200 text-sm">Batal</button>
                  <button onClick={saveTemplate} disabled={tplSaving}
                    data-testid="btn-save-tpl"
                    className="h-10 px-5 rounded-lg bg-violet-600 hover:bg-violet-700 text-white text-sm font-medium inline-flex items-center gap-1.5 disabled:opacity-50">
                    <Save className="w-4 h-4" /> {tplSaving ? "Menyimpan..." : (tplModal.mode === "edit" ? "Perbarui Template" : "Simpan Template")}
                  </button>
                </div>
              </div>
            </div>
            {tplGuard.confirming && (
              <UnsavedGuardBar onStay={tplGuard.cancelClose} onDiscard={tplGuard.confirmDiscard} testid="tpl-modal-guard-bar" />
            )}
          </div>
        </div>
      )}
    </div>
  );
}
